import { useState } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChevronDown, ChevronUp, Shield, ArrowRight, Calendar, TrendingUp, Zap, BarChart3, Trophy, Target, Info, HelpCircle } from "lucide-react";
import { Link } from "wouter";

const FEATURED_SLOTS = [
  {
    name: "Dragon's Fire",
    rtp: "96,12 %",
    volatility: "vysoká",
    provider: "Red Tiger",
    probability: "63,40 %",
    thumb: "https://assets.slotslaunch.com/3802/dragons-fire.jpg",
    desc: "Dragon's Fire od Red Tiger je slot s draky a ohnivými mechanikami, který nabízí vysoký potenciál výher prostřednictvím bonusových kol s multiplikátory. Hra se vyznačuje dynamickými symboly a speciálními funkcemi, které mohou výrazně zvýšit výslednou výhru při aktivaci bonusového kola.",
    probabilityExplain: "Pokud předpokládáme, že slot s vysokou volatilitou aktivuje bonus přibližně jednou za 100 spinů, lze odhadnout pravděpodobnost aktivace alespoň jednoho bonusu během 100 spinů. Použitý vzorec je 1 − (1 − 1 / 100)¹⁰⁰. Výsledkem je přibližně 63,40 %, což znamená, že během relace 100 spinů existuje matematická pravděpodobnost kolem 63 %, že se bonus aktivuje alespoň jednou.",
    cta: "Hrajte Dragon's Fire s casino bonusem",
    ctaLink: "/bonusy",
  },
  {
    name: "Sweet Bonanza",
    rtp: "96,48 %",
    volatility: "vysoká",
    provider: "Pragmatic Play",
    probability: "63,40 %",
    thumb: "https://assets.slotslaunch.com/5706/sweet-bonanza.jpg",
    desc: "Sweet Bonanza patří mezi populární sloty s vysokým potenciálem výher díky systému tumble (kaskádové výhry) a náhodným multiplikátorům. Výhry se vyhodnocují podle skupin symbolů a vítězné kombinace se po každém kole odstraní, což může vést k dalším kombinacím.",
    probabilityExplain: "Hlavní hodnota hry pochází z bonusového kola free spins, kde se objevují náhodné multiplikátory, které mohou výrazně zvýšit výhru. Pokud předpokládáme frekvenci bonusu přibližně 1× za 100 spinů, lze pravděpodobnost aktivace alespoň jednoho bonusu během 100 spinů vypočítat stejným vzorcem 1 − (1 − 1 / 100)¹⁰⁰. Výsledek je opět přibližně 63,40 %, což naznačuje, že bonus se může objevit relativně často, i když velké výhry zůstávají vzácné.",
    cta: "Vyzkoušejte Sweet Bonanza s free spiny",
    ctaLink: "/bonusy",
  },
  {
    name: "Forge of Olympus",
    rtp: "96,00 %",
    volatility: "vysoká",
    provider: "Pragmatic Play",
    probability: "63,40 %",
    thumb: "https://assets.slotslaunch.com/15216/forge-of-olympus.jpg",
    desc: "Forge of Olympus je slot od Pragmatic Play zasazený do světa řecké mytologie. Hra nabízí unikátní mechaniku kování, kde speciální symboly aktivují bonusové funkce s vysokými multiplikátory. Bonusové kolo free spinů je hlavním zdrojem potenciálu výher a může přinést velmi výrazné výhry.",
    probabilityExplain: "Stejně jako u jiných slotů s vysokou volatilitou můžeme předpokládat frekvenci bonusu přibližně 1× za 100 spinů. Pravděpodobnost aktivace alespoň jednoho bonusu během 100 spinů se tedy vypočítá pomocí vzorce 1 − (1 − 1 / 100)¹⁰⁰, což dává výsledek přibližně 63,40 %.",
    cta: "Získejte free spiny pro Forge of Olympus",
    ctaLink: "/bonusy",
  },
  {
    name: "Book of Dead",
    rtp: "96,21 %",
    volatility: "vysoká",
    provider: "Play'n GO",
    probability: "63,40 %",
    thumb: "/img/book-of-dead.svg",
    desc: "Book of Dead patří mezi klasické sloty s vysokým potenciálem výher. Hra využívá mechaniku rozšiřujících se symbolů během bonusových free spinů, které mohou zaplnit celý válec a vytvořit velmi vysoké výhry.",
    probabilityExplain: "Nejdůležitější částí hry je právě bonusová funkce free spins. Pokud předpokládáme frekvenci bonusu přibližně 1× za 100 spinů, lze pravděpodobnost aktivace alespoň jednoho bonusu během 100 spinů opět odhadnout pomocí vzorce 1 − (1 − 1 / 100)¹⁰⁰. Výsledek je přibližně 63,40 %.",
    cta: "Hrajte Book of Dead s casino bonusy",
    ctaLink: "/bonusy",
  },
  {
    name: "Sugar Rush",
    rtp: "96,50 %",
    volatility: "vysoká",
    provider: "Pragmatic Play",
    probability: "63,40 %",
    thumb: "https://assets.slotslaunch.com/35001/sugar-rush.jpg",
    desc: "Sugar Rush je cukrářský slot od Pragmatic Play s unikátní mřížkou 7×7 a mechanikou kaskádových výher. Speciální bonusové kolečko náhodně přidává multiplikátory na symboly, které se pak aktivují ve free spin bonusu a mohou výrazně zvýšit výhru.",
    probabilityExplain: "Pokud předpokládáme frekvenci bonusu přibližně 1× za 100 spinů, pravděpodobnost aktivace alespoň jednoho bonusu během 100 spinů lze vypočítat pomocí vzorce 1 − (1 − 1 / 100)¹⁰⁰. Výsledek je přibližně 63,40 %, což ukazuje, že bonus může být relativně dostupný, i když skutečně velké výhry zůstávají méně časté.",
    cta: "Vyzkoušejte Sugar Rush s free spiny",
    ctaLink: "/bonusy",
  },
];

const POTENTIAL_FACTORS: { title: string; desc: string; cta?: string; ctaLink?: string }[] = [
  {
    title: "Volatilita",
    desc: "Volatilita popisuje styl výher ve slotu. Sloty s vysokou volatilitou obvykle nabízejí větší výhry, ale tyto výhry se objevují méně často a mohou být odděleny delšími obdobími bez výher.",
    cta: "Viz: sloty s vysokou volatilitou",
    ctaLink: "/automaty",
  },
  {
    title: "RTP",
    desc: "RTP (Return to Player) představuje teoretickou návratnost hry v dlouhodobém horizontu. Tento údaj popisuje průměrný podíl sázek, který se hráčům vrací během velkého množství spinů, ale neurčuje frekvenci velkých výher.",
    cta: "Viz: sloty s vysokým RTP",
    ctaLink: "/automaty",
  },
  {
    title: "Maximální multiplikátory",
    desc: "Multiplikátory jsou mechaniky, které mohou výrazně zvýšit výhru během jedné kombinace. Ve slotových hrách se často objevují během bonusových kol nebo speciálních funkcí a mohou násobit základní výhru několikanásobně.",
  },
  {
    title: "Bonusová kola",
    desc: "Bonusová kola patří mezi hlavní mechaniky, které vytvářejí nejvyšší výhry ve slotech. Během těchto funkcí mohou být aktivovány multiplikátory, speciální symboly nebo další herní prvky, které výrazně zvyšují potenciální výhru.",
  },
  {
    title: "Jackpoty",
    desc: "Jackpoty, zejména progresivní jackpoty, mohou dosahovat velmi vysokých částek. Tyto jackpoty se postupně zvyšují, protože část každé sázky přispívá do společného jackpotového fondu, který roste, dokud jej některý hráč nevyhraje.",
    cta: "Viz: progresivní sloty",
    ctaLink: "/automaty",
  },
];

const RELATED_SLOTS = [
  { type: "High RTP Slots", desc: "Sloty s vyšší teoretickou návratností hráči" },
  { type: "High Volatility Slots", desc: "Sloty s méně častými, ale potenciálně vyššími výhrami" },
  { type: "Progressive Slots", desc: "Sloty s jackpoty, které rostou s každou sázkou hráčů" },
  { type: "Megaways Slots", desc: "Sloty s tisíci výherních kombinací díky dynamickým válcům" },
  { type: "Free Slots", desc: "Sloty, které lze hrát zdarma bez nutnosti vkladu" },
];

const FAQS = [
  { q: "Který slot vyplácí nejvíce?", a: "Neexistuje jeden slot, který by vždy vyplácel nejvíce. Některé hry mají vyšší maximální výhru, ale výsledky jsou vždy náhodné." },
  { q: "Vyplácejí sloty s vysokým potenciálem častěji?", a: "Obvykle ne. Sloty s vyšší maximální výhrou často vyplácejí méně často." },
  { q: "Ovlivňuje čas hraní výhry?", a: "Ne. Výsledky jsou generovány RNG a každý spin je nezávislý." },
  { q: "Existuje strategie pro výhru ve slotech?", a: "Neexistuje strategie, která by zaručila výhru. Nejlepší přístup je správa bankrollu a realistická očekávání." },
];

const TIPS = [
  "Správná správa bankrollu – nastavte si denní nebo týdenní limit a nepřekračujte jej.",
  "Výběr slotů odpovídajících hernímu stylu – pokud preferujete častější výhry, zvolte sloty se střední nebo nízkou volatilitou.",
  "Využívání dostupných bonusů nebo free spinů – tyto nabídky mohou prodloužit dobu hraní bez nutnosti dalšího vkladu.",
  "Realistická očekávání – maximální výhra je teoretická hodnota a objevuje se velmi vzácně.",
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eef1f7] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4"
        data-testid={`faq-sloty-${q.slice(0, 20).replace(/\s/g, "-")}`}
      >
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#003da5] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 text-sm leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

export default function AutomatySlotyVyplacejiNejvicePage() {
  useSEO({
    title: "Sloty s Nejvyšším RTP 2026 – Které automaty nejvíce vyplácejí",
    description: "Které online sloty vyplácejí nejvíce? Přehled 5 nejlepších automatů s analýzou RTP, volatility a pravděpodobnosti bonusů. 18+",
    url: "https://hrajbonus.com/automaty/sloty-ktere-vyplaceji-nejvice",
  });

  return (
    <div className="min-h-screen bg-[#f5f7fb]" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 text-white/60 text-sm font-semibold">
              <Link href="/automaty" className="hover:text-white transition-colors" data-testid="link-back-automaty">Automaty</Link>
              <span>/</span>
              <span className="text-white/90">Sloty, které vyplácejí nejvíce</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold" data-testid="badge-aktualizovano">
              <Calendar className="w-3 h-3" /> Aktualizováno: 14. března 2026
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5" data-testid="text-page-title">
            Sloty, které vyplácejí nejvíce
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-6 max-w-3xl">
            Mnoho hráčů hledá sloty, které vyplácejí nejvíce, protože chtějí hry s potenciálem opravdu velkých výher. Je však důležité pochopit, co tento pojem ve skutečnosti znamená. Ve většině případů nejde o automaty, které vyplácejí častěji, ale o hry s vyšším potenciálem maximální výhry.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#featured-slots">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                <Trophy className="w-3.5 h-3.5" /> Top 5 slotů
              </div>
            </a>
            <a href="#metodologie">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                <BarChart3 className="w-3.5 h-3.5" /> Metodologie
              </div>
            </a>
            <a href="#faq">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                <HelpCircle className="w-3.5 h-3.5" /> FAQ
              </div>
            </a>
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      <nav className="bg-[#f5f7fb] border-b border-[#dde4ee] sticky top-16 z-20" data-testid="automaty-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {[
              { href: "/automaty", label: "Automaty přehled", active: false },
              { href: "/automaty/jak-vyhrat", label: "Jak vyhrát", active: false },
              { href: "/automaty/sloty-ktere-vyplaceji-nejvice", label: "Sloty které vyplácejí nejvíce", active: true },
            ].map(({ href, label, active }) => (
              <Link key={href} href={href}>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-[#003da5] text-white"
                      : "text-gray-600 hover:bg-white hover:text-[#003da5]"
                  }`}
                  data-testid={`automaty-nav-${href.replace(/\//g, "-")}`}
                >
                  {label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-8">

        <section className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
          <p className="text-gray-600 text-sm leading-relaxed">
            V tomto průvodci ukazujeme, jak lze pomocí jednoduchého matematického modelu odhadnout pravděpodobnost určitých událostí ve slotových hrách. Tento přístup pomáhá hráčům lépe pochopit potenciál jednotlivých her bez spoléhání na mýty nebo neexistující strategie. Ten může být spojen s vysokými multiplikátory, bonusovými koly nebo jackpoty. Tyto mechaniky mohou přinést velmi vysoké výhry, ale zároveň mohou znamenat delší období bez výraznějších výher.
          </p>
        </section>

        <section id="featured-slots" data-testid="section-featured-slots">
          <h2 className="text-3xl font-black text-gray-900 mb-6">Top 5 slotů s nejvyšším potenciálem</h2>
          <div className="space-y-6">
            {FEATURED_SLOTS.map((slot, i) => (
              <div key={slot.name} className="bg-white rounded-2xl border border-[#dde4ee] overflow-hidden shadow-sm" data-testid={`card-slot-${i}`}>
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-56 lg:w-64 flex-shrink-0 bg-gray-100">
                    <img
                      src={slot.thumb}
                      alt={`${slot.name} – online slot od ${slot.provider}`}
                      className="w-full h-48 md:h-full object-cover"
                      data-testid={`img-slot-${i}`}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  </div>
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                      <h3 className="text-xl font-black text-gray-900" data-testid={`text-slot-name-${i}`}>{slot.name}</h3>
                      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1 text-green-700 text-xs font-bold" data-testid={`badge-probability-${i}`}>
                        <Target className="w-3 h-3" /> Pravděpodobnost: {slot.probability}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-blue-700 text-xs font-semibold" data-testid={`badge-rtp-${i}`}>
                        <TrendingUp className="w-3 h-3" /> RTP: {slot.rtp}
                      </span>
                      <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-amber-700 text-xs font-semibold" data-testid={`badge-volatility-${i}`}>
                        <Zap className="w-3 h-3" /> Volatilita: {slot.volatility}
                      </span>
                      <span className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-full px-3 py-1 text-gray-600 text-xs font-semibold" data-testid={`badge-provider-${i}`}>
                        {slot.provider}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">{slot.desc}</p>
                    <div className="bg-[#f3f6fb] rounded-xl p-4 border border-[#dde4ee]/50 mb-4">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                        <p className="text-gray-600 text-xs leading-relaxed">{slot.probabilityExplain}</p>
                      </div>
                    </div>
                    <Link href={slot.ctaLink}>
                      <div className="inline-flex items-center gap-2 text-[#003da5] font-bold text-sm hover:underline cursor-pointer" data-testid={`link-cta-${i}`}>
                        <ArrowRight className="w-4 h-4" /> {slot.cta}
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="co-znamena" data-testid="section-co-znamena">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Co znamená, když slot „vyplácí více"</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Když hráči mluví o sloty, které vyplácejí nejvíce, obvykle tím myslí hry s vyšším potenciálem maximální výhry. Neznamená to však, že tyto sloty vyplácejí častěji. Ve skutečnosti je důležité rozlišovat mezi velikostí výher a frekvencí výher. Některé automaty mohou nabídnout velmi vysokou maximální výhru, ale zároveň mohou mít delší období bez výrazných výher. Mnoho hráčů si tento pojem navíc plete s RTP, což je odlišný ukazatel, který popisuje teoretickou návratnost hry v dlouhodobém horizontu.
            </p>
            <Link href="/automaty">
              <div className="inline-flex items-center gap-2 text-[#003da5] font-bold text-sm hover:underline cursor-pointer" data-testid="link-sloty-vysoke-rtp">
                <ArrowRight className="w-4 h-4" /> Viz také: sloty s vysokým RTP
              </div>
            </Link>
          </div>
        </section>

        <section id="potencial" data-testid="section-potencial">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Jak se definuje potenciál výher slotu</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Potenciál výher slotu je výsledkem několika herních faktorů, které společně určují strukturu výplat a celkový styl hry. Tyto prvky určují, zda je slot zaměřený spíše na častější menší výhry, nebo na méně časté, ale výrazně vyšší výplaty.
            </p>
            <div className="space-y-4">
              {POTENTIAL_FACTORS.map((factor, i) => (
                <div key={factor.title} className="bg-[#f3f6fb] rounded-xl p-4 border border-[#dde4ee]/50" data-testid={`factor-${i}`}>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{factor.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{factor.desc}</p>
                  {factor.cta && factor.ctaLink && (
                    <Link href={factor.ctaLink}>
                      <div className="inline-flex items-center gap-2 text-[#003da5] font-bold text-xs hover:underline cursor-pointer mt-2" data-testid={`link-factor-cta-${i}`}>
                        <ArrowRight className="w-3.5 h-3.5" /> {factor.cta}
                      </div>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="metodologie" data-testid="section-metodologie">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Naše metodologie</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Pro odhad pravděpodobností určitých událostí ve slotových hrách používáme jednoduchý matematický model. Tento model vychází z předpokládané frekvence bonusového kola, která je odvozena od volatility hry.
            </p>
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Orientační frekvence bonusu:</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <div className="text-red-700 font-black text-lg">1 / 100</div>
                  <div className="text-red-600 text-xs font-semibold mt-1">Vysoká volatilita</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <div className="text-amber-700 font-black text-lg">1 / 200</div>
                  <div className="text-amber-600 text-xs font-semibold mt-1">Střední volatilita</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <div className="text-green-700 font-black text-lg">1 / 400</div>
                  <div className="text-green-600 text-xs font-semibold mt-1">Nízká volatilita</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#001a4d] to-[#003da5] rounded-2xl p-6 text-center" data-testid="formula-box">
              <div className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-3">Vzorec pro výpočet pravděpodobnosti</div>
              <div className="text-white font-black text-3xl sm:text-4xl mb-3" style={{ fontFamily: "serif" }}>
                1 − (1 − 1 / N)<sup>x</sup>
              </div>
              <p className="text-white/70 text-sm max-w-lg mx-auto">
                Kde <strong className="text-white">N</strong> představuje odhadovanou frekvenci bonusu a <strong className="text-white">X</strong> počet spinů během relace. Tento model samozřejmě nepředpovídá skutečné výsledky hry, ale pomáhá lépe pochopit pravděpodobnosti a strukturu slotových her.
              </p>
            </div>
          </div>
        </section>

        <section id="kasina" data-testid="section-kasina">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Nejlepší kasina pro sloty s vysokým potenciálem výher</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              Pro hraní slotů s vysokým potenciálem výher je důležité vybrat licencované online kasino, které nabízí kvalitní herní software od renomovaných poskytovatelů. Spolehlivá kasina obvykle spolupracují s vývojáři jako Pragmatic Play, Play'n GO nebo NetEnt a zároveň poskytují transparentní podmínky výběru i bezpečné platební metody. Výhodou může být také široká nabídka bonusů, například uvítací balíčky nebo free spiny, které umožňují vyzkoušet různé sloty bez nutnosti vysokého vkladu.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/automaty">
                <div className="inline-flex items-center gap-2 text-[#003da5] font-bold text-sm hover:underline cursor-pointer" data-testid="link-zobrazit-kasina">
                  <ArrowRight className="w-4 h-4" /> Zobrazit nejlepší online kasina
                </div>
              </Link>
              <Link href="/bonusy">
                <div className="inline-flex items-center gap-2 text-[#003da5] font-bold text-sm hover:underline cursor-pointer" data-testid="link-casino-bonusy">
                  <ArrowRight className="w-4 h-4" /> Získat casino bonusy a free spiny
                </div>
              </Link>
            </div>
          </div>
        </section>

        <section id="ocekavani" data-testid="section-ocekavani">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Jaká očekávání by měli mít hráči</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Sloty s vysokým potenciálem výher jsou obvykle spojeny s vyšším rizikem a delšími obdobími bez výher. Je proto důležité mít realistická očekávání. Maximální výhra uvedená u slotu je vždy pouze teoretická hodnota a ve skutečnosti se objevuje velmi vzácně. V některých hrách je navíc nutné vsadit maximální sázku, aby bylo možné nejvyšší výhru aktivovat. Důležité je také vědět, že žádný vzorec, čas hraní ani počet hráčů nemá vliv na výsledky hry. Každý spin je generován náhodně a je zcela nezávislý na předchozích výsledcích.
            </p>
          </div>
        </section>

        <section id="tipy" data-testid="section-tipy">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Tipy a strategie pro hraní slotů</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              I když neexistuje strategie, která by dokázala zaručit výhru, některé principy mohou pomoci hrát zodpovědněji a efektivněji. Mezi základní pravidla patří správná správa bankrollu, výběr slotů odpovídajících hernímu stylu a využívání dostupných bonusů nebo free spinů. Tyto faktory mohou prodloužit dobu hraní a pomoci lépe kontrolovat rozpočet.
            </p>
            <ul className="space-y-3">
              {TIPS.map((tip, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-700" data-testid={`tip-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-[#003da5]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#003da5] text-xs font-black">{i + 1}</span>
                  </div>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              <Link href="/automaty">
                <div className="inline-flex items-center gap-2 text-[#003da5] font-bold text-sm hover:underline cursor-pointer" data-testid="link-strategie-sloty">
                  <ArrowRight className="w-4 h-4" /> Viz strategie pro sloty
                </div>
              </Link>
            </div>
          </div>
        </section>

        <section id="souvisejici" data-testid="section-souvisejici">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-5">Související typy slotů</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-related-slots">
                <thead>
                  <tr className="border-b-2 border-[#003da5]/20">
                    <th className="text-left py-3 px-4 font-bold text-gray-900">Typ slotu</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-900">Popis</th>
                  </tr>
                </thead>
                <tbody>
                  {RELATED_SLOTS.map((slot, i) => (
                    <tr key={slot.type} className="border-b border-[#eef1f7] last:border-0" data-testid={`row-related-${i}`}>
                      <td className="py-3 px-4 font-semibold text-[#003da5] whitespace-nowrap">{slot.type}</td>
                      <td className="py-3 px-4 text-gray-600">{slot.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section data-testid="section-zaver">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Závěr</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Sloty, které vyplácejí nejvíce, nejsou nutně ty, které vyplácejí nejčastěji. Tento pojem obvykle označuje hry s vyšším potenciálem maximální výhry, který může být spojen s multiplikátory, bonusovými funkcemi nebo progresivními jackpoty. Tyto sloty mohou nabídnout velmi vysoké výhry, ale zároveň přinášejí vyšší riziko a delší období bez výher. Pokud hráči pochopí, jak tyto mechaniky fungují, mohou mít realističtější očekávání a přistupovat ke hraní informovaněji.
            </p>
          </div>
        </section>

        <section id="faq" data-testid="section-faq">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-5">FAQ</h2>
            <div>
              {FAQS.map(item => <FaqItem key={item.q} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        <div className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm flex items-start gap-4">
          <Shield className="w-5 h-5 text-[#003da5] mt-0.5 flex-shrink-0" />
          <p className="text-gray-600 text-sm leading-relaxed">
            <span className="font-bold text-gray-900">Pouze pro hráče 18+. </span>
            Hrajte zodpovědně. Nastavte si limity a sázejte jen tolik, kolik si můžete dovolit prohrát. Pokud potřebujete pomoc, obraťte se na{" "}
            <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gamcare-sloty">GamCare</a>.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm">
          <p className="text-sm text-gray-600 leading-relaxed">
            <span className="font-bold text-gray-900">Zahrajte si tyto sloty v ověřeném kasinu: </span>
            <a href="/kasina/fastslots" className="text-[#003da5] hover:underline" data-testid="sloty-link-fastslots">Fast Slots</a>
            {" · "}
            <a href="/kasina/onlyspins" className="text-[#003da5] hover:underline" data-testid="sloty-link-onlyspins">OnlySpins</a>
            {" · "}
            <a href="/kasina/instant-casino" className="text-[#003da5] hover:underline" data-testid="sloty-link-instant">Instant Casino</a>
            {" · "}
            <a href="/kasina/golden-panda" className="text-[#003da5] hover:underline" data-testid="sloty-link-golden">Golden Panda</a>
            {" · "}
            <a href="/kasina" className="text-[#003da5] hover:underline font-semibold" data-testid="sloty-link-kasina">Všechna kasina</a>
            {" · "}
            <a href="/automaty/jak-vyhrat" className="text-[#003da5] hover:underline" data-testid="sloty-link-jak-vyhrat">Jak vyhrát na automatech</a>
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium pb-4">
          <Shield className="w-3.5 h-3.5" />
          <span>Pouze pro hráče 18+. Hrajte zodpovědně. Podmínky se vztahují. |{" "}
            <a href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-gambleaware-sloty">GambleAware</a>
          </span>
        </div>

      </div>
      <Footer />
    </div>
  );
}